import { Component, inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FocusService } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { dependencies } from '../../../../class/workflow.dependency';
import { CorePageBaseComponent } from '../core-page-base.component';
import { StatefulService } from '@app/service/stateful.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { AllowedStreamlinePrefillTypes } from '@workflow/ecp-shared/enum/enum';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'cu-dynamic-page-action-bar',
  templateUrl: './page-action-bar.component.html',
  standalone: false
})
export class PageActionBarComponent extends CorePageBaseComponent implements OnInit {
  private logger = inject(SawDust);
  public statefulService = inject(StatefulService);
  public analyticsMetadataService = inject(AnalyticsMetadataService);
  private readonly focusService = inject(FocusService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly workflowLoader = inject(WorkflowLoaderService);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * indicate if action bar should be visible
   */
  @Input() public showActionBar = true;

  private reviewLabel: string;
  /**
   * Loading state of the button
   */
  public isLoading = false;
  /**
   * Disabled state of the button
   */
  public isDisabled = false;

  public removeCoApplicantLabel: string;

  public ngOnInit(): void {
    const page = this.workflowPageNavigationService.getReviewPage();
    if (page) {
      this.reviewLabel = `Back to ${page.title}`;
    }
    // Adding Router events here so we can track when the page navigation ends
    // This will allow better control ocer when to re-enable the buttons
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.loadingNextButton(false);
      }
    });

    this.removeCoApplicantLabel = this.getRemoveCoAppTitle();
    this.handleStreamlined();
  }

  private get isReviewPage() {
    return this.page?.config?.customPageType === 'legacy-review';
  }
  /** continue button label  */
  public get continueLabel(): string {
    if (!this.page.config.next) {
      return 'Save and Continue';
    }

    /** application review page update button label */
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      if (!this.isReviewPage) {
        return 'Save Changes';
      }
    }

    if (typeof this.page.config.next === 'string') {
      return this.page.config.next;
    }

    return dependencies.logicService.evaluate(this.page.config.next, { page: this.page }, 'single', null, this.page.pageIterationId)[0];
  }

  /** back button label */
  public get backLabel(): string {
    /** application review page update button label */
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      if (!this.isReviewPage) {
        return this.reviewLabel || 'Back to Application Review';
      }
    }

    return this.page.config.previous;
  }

  /**
   * Set the loading and disabled state of the Next button
   * @param active boolean
   */
  private loadingNextButton(active: boolean): void {
    this.isDisabled = active;
    this.isLoading = active;
  }

  /** When Back is clicked */
  public onBackClicked(): void {
    this.widgetEventService.emitPreviousButtonClickedClicked({ flow: this.flow, page: this.page });
    if (this.page.config.pagePreviousClicked) {
      this.evaluateClickedFn(this.page.config.pagePreviousClicked, this.navigationPrevious);
      return;
    }
    const fragment = this.route.snapshot.fragment;

    if (this.flow?.config?.streamlined && (!fragment || this.isReviewPage)) {
      const prevPageId = this.getPrevPageForStreamlined();
      if (prevPageId && prevPageId !== 'unknown') {
        this.workflowPageNavigationService.goToPage(prevPageId);
        return;
      }
    }
    this.navigationPrevious();
  }

  /**
   * Since "EA" button component disabledButton still triggers onClick
   * handler (as a bug), makes sure page is complete befor executing continue logic
   * @returns
   */
  public onContinueClicked(): void {
    // If PID Verifiction page, DO NOT disable the button on click
    if (this.page.nextPage?.path !== 'application-review') {
      this.loadingNextButton(true);
    }

    // Page submit will trigger page
    if (this.alwaysEnablePageNext) {
      this.workflowPageNavigationService.pageSubmitted = true;
    }

    // If the page is not complete, then set to disabled
    if (!this.page.helper.isPageComplete) {
      // If there are invalid fields, find the first and focus on click
      if (this.page.helper.invalidFields?.length > 0) {
        const focusId =
          this.page.helper.invalidFields[0]?.config?.type?.toLowerCase() === 'radio'
            ? this.page.helper.invalidFields[0].id + '-0'
            : this.page.helper.invalidFields[0].id;
        this.focusService.setFocus(focusId);
      }
      // ensure the workflow loader is hidden on incomplete, regardless of circumstance
      this.workflowLoader.hide();
      this.loadingNextButton(false);
      return;
    }

    const data = this.analyticsMetadataService.getMetadata(this.page);
    this.googleTrackingService.publishEvent(data[0], data[1]);

    if (this.page.helper.touchedFields.length === 0) {
      /// marking page touched when user clicks continue button
      this.page.dirty = true;
    }
    this.workflowPageNavigationService.pageSubmitted = true;
    this.widgetEventService.emitNextButtonClicked({ flow: this.flow, page: this.page });

    if (this.page.config.pageNextClicked) {
      this.evaluateClickedFn(this.page.config.pageNextClicked, this.navigationNext);
      return;
    }

    this.navigationNext();
  }

  private evaluateClickedFn(logicFn, defaultNavigationFn?: () => void) {
    // Timeout to allow events to complete and virtual keyboard to close on mobile
    // before landing on the previous page so that scroll position does not persist
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dependencies.logicService.evaluate<Promise<any>>(
        logicFn,
        {
          page: this.page,
          flow: this.flow,
          defaultNavigationFn,
          pageIteration: this.page.pageIterationId
        },
        'generic',
        null,
        this.page.pageIterationId
      );

      // Reset the button state if a modal is opened
      if (this.page?.modalVisible) {
        this.loadingNextButton(false);
      }
    }, 400);
  }

  private get alwaysEnablePageNext(): boolean {
    return this.flow.config.alwaysEnablePageNext || this.page.config.alwaysEnablePageNext;
  }

  private navigationNext = () => {
    this.workflowPageNavigationService.updateNavState(this.page, true);
    if (this.page.helper.isPageComplete && this.page.helper.hasNextPage) {
      this.workflowPageNavigationService.navigateNextPage(!this.isReviewPage && this.route.snapshot.fragment);
    }
  };

  private navigationPrevious = () => {
    if (this.page.helper.hasPreviousPage) {
      this.workflowPageNavigationService.navigatePreviousPage(!this.isReviewPage && this.route.snapshot.fragment);
    }
  };

  public get previousPageLabel(): string {
    if (this.flow?.config?.streamlined) {
      const prevPageId = this.getPrevPageForStreamlined();
      if (prevPageId && prevPageId !== 'unknown') {
        const prevPage = this.flow.pages.find((p) => p.config.id === prevPageId);
        return prevPage?.title || prevPage?.customTitleOrDefault || 'Previous Page';
      }
    }
    return this.page.previousPage?.customTitleOrDefault || this.page.previousPage?.title || 'Previous Page';
  }

  /**
   * Determines the previous page to navigate to in a streamlined workflow,
   * specifically handling cases where pages may have failed during autofill.
   * Returns the ID of the previous page that should be navigated to, or null if not applicable.
   */
  private getPrevPageForStreamlined(): string | null {
    // Get the list of pages that failed during autofill from stateful service
    const failedPages = (this.statefulService.wrap('ss:pages-failed-on-autofill').value + '').split(',').filter((id) => !!id);
    // Get the list of pages that can be skipped in streamlined flow
    const skipPages = this.flow.config.streamlinedSkipPages || [];
    // Check if the previous page is in the skipPages list
    if (!!this.page.previousPage && skipPages.includes(this.page.previousPage.config.id)) {
      // Find the index of the previous page in the skipPages array
      const currentPageIndex = skipPages.indexOf(this.page.previousPage.config.id);
      let prevPageId: string | null = null;
      let maxIndex = -1;
      // Iterate through failedPages to find the closest previous failed page in skipPages
      for (const pageId of failedPages) {
        const idx = skipPages.indexOf(pageId);
        // Only consider failed pages that come before the current page in skipPages
        if (idx > -1 && idx <= currentPageIndex && idx > maxIndex) {
          maxIndex = idx;
          prevPageId = pageId;
        }
      }
      return prevPageId;
    }
    return 'unknown';
  }
  /**
   * Check Previous Page button visibility
   * @returns boolean
   */
  /**
   * Determines the visibility of the "Previous Page" button based on the current workflow state.
   *
   * The button is shown or hidden according to several conditions:
   * - If the workflow is in a streamlined mode and the current page is part of a streamlined prefill path
   *   (with supported auto-fill values), and the user is not editing from the review page, and there is no URL fragment,
   *   the button is shown only if there is a previous page in the streamlined flow.
   * - If the user is editing from the review page, the button is hidden unless the first incomplete page's ID includes 'review'.
   * - Otherwise, the button's visibility is determined by the page helper's `isPreviousButtonVisible` property.
   * @returns boolean `true` if the "Previous Page" button should be visible; otherwise, `false`.
   */
  public checkPreviousPageButtonVisibility(): boolean {
    const isEditReview = this.statefulService.wrap('ss:edit-review-page').value;
    // Do not show back button if previous page is in stream skip logic.
    // Show Backbutton when user edit from review
    const supportedAutoFillValue: string[] = AllowedStreamlinePrefillTypes;
    if (this.flow?.config?.streamlinedPrefillPath) {
      const streamlineSkip = this.statefulService.wrap(this.flow?.config?.streamlinedPrefillPath, null, this.page?.pageIterationId);

      // Check if the streamlinzzed flow is enabled,
      // the current value is supported for auto-fill,
      // the user is not editing a review,
      // and there is no URL fragment in the current route.
      if (
        this.flow?.config?.streamlined &&
        supportedAutoFillValue.includes(streamlineSkip.value?.toString()) &&
        !isEditReview &&
        !this.route.snapshot.fragment
      ) {
        // Get the previous page ID for streamlined flow
        const prevPageId = this.getPrevPageForStreamlined();

        // If a previous page ID exists, allow back action
        if (prevPageId !== 'unknown') {
          if (prevPageId) {
            return true;
          }
          return false;
        }
      }
    }
    if (isEditReview) {
      const firstInCompletePage = this.workflowPageNavigationService.getFirstInCompletePage();
      if (firstInCompletePage) {
        if (!firstInCompletePage.id.includes('review')) {
          return false;
        }
      }
    }
    // ensure we always show the back label if coming from review
    // previous logic could hide it when the "previous" page is product selection
    if (this.reviewLabel && this.backLabel && this.reviewLabel === this.backLabel) {
      return true;
    }
    return this.page.helper.isPreviousButtonVisible;
  }

  /**
   * Retrieves the title for the action to remove a co-applicant.
   * If there is an additional co-applicant, the title will indicate the removal of the second co-applicant.
   * Otherwise, it will indicate the removal of a single co-applicant.
   * @returns The title for the remove co-applicant action.
   * */
  public getRemoveCoAppTitle(): string {
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant').value as boolean;
    const isAdditionalCoApplicantPage = this.page.id.includes('oao-application-fields-additional-co-app');
    if (hasAdditionalCoApplicant && isAdditionalCoApplicantPage) {
      return 'Remove second co-applicant and continue';
    }
    return 'Remove co-applicant and continue';
  }

  /**
   * This will handle some of the logic if we are in a streamlined template
   * First - check if streamlined, then show the loader, verify if we should be showing the loader
   * we use an interval specifically for the initial template loading as we can accidentally get stuck
   * if the data loads before the workflow - technically could be done without the interval in later pages.-
   * Once we know the page is loaded, we check if we can skip - behind the loader.
   */
  private handleStreamlined(): void {
    const supportedAutoFillValue: string[] = AllowedStreamlinePrefillTypes;
    this.flow?.closeLoader();
    // here we call continue
    if (this.flow?.config?.streamlined && (!this.route.snapshot.fragment || this.page.config.lockOnPrefill)) {
      const streamlineSkip = this.statefulService.wrap(this.flow.config.streamlinedPrefillPath, null, this.page.pageIterationId);
      const existingFailedPages = this.statefulService.wrap('ss:pages-failed-on-autofill').value?.toString() || '';
      if (this.flow.config.streamlinedSkipPages.includes(this.page.id)) {
        if (existingFailedPages.includes(this.page.id)) {
          this.logger.logTrace(`Streamline flow incomplete page ${this.page.id}`, { page: this.page.id });
          return;
        }
        // we can't show this on every page - so ensure we want to trigger by supported skip page
        this.handleLoader(true, supportedAutoFillValue.includes(streamlineSkip.value?.toString()));
      }
      let personalInfoAttempt = 0;
      let formLoadingAttemptCount = 0;
      const pollHandle = setInterval(() => {
        const skipPageSupported = streamlineSkip.value;
        if (
          supportedAutoFillValue.includes(skipPageSupported?.toString()) &&
          this.flow.config.streamlinedSkipPages.includes(this.page.id)
        ) {
          this.handleLoader(true, supportedAutoFillValue.includes(skipPageSupported?.toString()));
          if (!this.page.helper.pageVisibleFields || this.page.helper.pageVisibleFields.length === 0) {
            if (formLoadingAttemptCount > (1000 * 60) / 20) {
              this.logger.warn(`Streamline flow timed out loading elements, are all fields hidden?`, { page: this.page.id, component: 'PageActionBarComponent' });
              // if we have tried for 1 whole minute, then stop trying and show the screen
              clearInterval(pollHandle);
              this.handleLoader(false);
            }
            formLoadingAttemptCount++;
            return;
          }
          const identificationCheckPages = ['ola-secure-loan-personal-info', 'oao-application-fields'];
          // This is the polling logic to have 3 more attempts so identification fields will get loaded in next 60ms.
          // Add retry attempt only for ola-secure-loan-personal-info and oao-application-fields.
          if (
            identificationCheckPages.includes(this.page.config.id) &&
            (!this.page.helper.isPageComplete || !this.page.helper.pageVisibleFields.some((t) => t.config?.payload?.isLoading)) &&
            personalInfoAttempt < 3
          ) {
            personalInfoAttempt++;
            return;
          }

          clearInterval(pollHandle);

          const existingPages = this.statefulService.wrap('ss:pages-failed-on-autofill').value?.toString() || '';
          const pageIds = existingPages.split(',');

          // check if the page is valid, if not, they should add missing data
          if (this.page.helper.isPageComplete && !pageIds.includes(this.page.config.id)) {
            this.logger.info(`Streamline flow completed page ${this.page.id}`, { page: this.page.id, component: 'PageActionBarComponent' });
            // we are good to skip page - note: this keeps the loader going intentionally
            this.onContinueClicked();
            return;
          } else {
            pageIds.includes(this.page.config.id) || pageIds.push(this.page.config.id);
            this.statefulService.wrap('ss:pages-failed-on-autofill').value = pageIds.join(',');
          }
        }
        clearInterval(pollHandle);
        this.handleLoader(false);
      }, 20);
      return;
    }
    if (this.page.id === 'oao-membership' && this.flow.id === 'oao' && this.isBrowser && !!sessionStorage.getItem('MembershipType')) {
      this.handleLoader(false);
    }
  }

  /** only call this if in streamlined, handles showing or hiding the loader */
  private handleLoader(show: boolean = false, prefill: boolean = false): void {
    const messages = [
      'Connecting to your profile...',
      'Retrieving your information...',
      'Importing to your application...',
      'Importing to your application...'
    ];
    let page = this.flow?.config?.streamlinedSkipPages?.indexOf(this.page.id);
    if (typeof page === 'undefined') page = -1;
    let message = page > -1 ? messages[page] : '';
    if (!prefill) {
      message = 'Loading please wait...';
    }
    if (show && page === 0) {
      // first page, start loading animation
      this.setLoaderforFirstPageWhenShow(message, prefill ? 'documentscan' : 'padded');
    } else if (show && page === 1) {
      this.setLoaderforSecondPageWhenShow(message, prefill ? 'documentscan' : 'default');
    } else if (show) {
      this.setLoaderDefaultWhenShow(page, message, prefill ? 'documentscan' : 'default');
    } else if (this.page.id !== 'oao-additional-accounts') {
      // note: we skip end on additional accounts, because that page will directly call end once the
      // accounts and services have been loaded and rendered
      this.workflowLoader.end(message);
    }
  }

  /** Set workflowloader and message when page is 0 position of skip pages*/
  private setLoaderforFirstPageWhenShow(message: string, animationId: string) {
    this.workflowLoader.start(message, {
      animationId,
      overlayMode: 'card',
      segments: this.flow.config.streamlinedSkipPages.length - 1
    });
  }

  /** Set workflowloader and message when page is 1 position of skip pages*/
  private setLoaderforSecondPageWhenShow(message: string, animationId: string) {
    if (['hide', 'end', 'clear', 'init'].includes(this.workflowLoader.state.type)) {
      this.workflowLoader.start(message, {
        animationId,
        overlayMode: 'card',
        segments: this.flow.config.streamlinedSkipPages.length - 1
      });
    } else if (this.workflowLoader.segmentInfo.activeSegment === 0) {
      this.workflowLoader.next(message);
    }
  }

  /** Set workflowloader and message when show is true */
  private setLoaderDefaultWhenShow(page, message: string, animationId: string) {
    if (['init', 'start', 'next', 'show'].includes(this.workflowLoader.state?.type)) {
      if (this.workflowLoader.state.options?.segments !== page) {
        // advance because we are on another page
        this.workflowLoader.next(message);
      }
    } else {
      this.workflowLoader.start('Loading please wait...', {
        animationId,
        overlayMode: 'card',
        segments: this.flow.config.streamlinedSkipPages.length - 1
      });
    }
  }
}
